import React, { useMemo } from 'react';
import { GivelifyLabel } from '@givelify/ui';
import { formatWithTimezone } from '@givelify/utils';
import { useTranslation } from 'react-i18next';
import { formatMoneyWithCommas } from 'utils/formatMoneyWithCommas';
import { DonationDetailsContainer } from './DonationDetailsStyles';
import { PageName } from './types';

export interface EnvelopeDetailProps {
    id: number;
    name: string;
    offeringId: number;
    amount: number;
    doneeId: number;
    externalId: string;
}

export interface EnvelopRowProps {
    receiptNumber: number;
    date: string;
    envelopes: EnvelopeDetailProps[];
    total: number;
    fees: number;
    pageName: PageName;
}

const formatMoney = (money: string | number) =>
    `$${formatMoneyWithCommas(money)}`;

const DonationDetails: React.FCC<EnvelopRowProps> = (props) => {
    const { t } = useTranslation();

    const isRefund = props.pageName === 'refund';

    const copy = useMemo(
        () => ({
            donationDetails: t('bankDeposits.text.donationDetails'),
            donation: t('bankDeposits.text.donation'),
            fees: t('bankDeposits.text.fees'),
            total: t('bankDeposits.text.total'),
            receipt: t('bankDeposits.text.receipt'),
            envelopeName: t('bankDeposits.text.envelopName'),
            donationDate: t('bankDeposits.text.donationDate'),
            refundAmount: t('bankDeposits.text.refundAmount'),
        }),
        [t],
    );
    const envelopes = props.envelopes;

    return (
        <DonationDetailsContainer isRefund={isRefund}>
            <GivelifyLabel
                className="mobileFirstRowHeader tableHeaderText donationDetailsHeader"
                text={copy.donationDetails}
            />
            {isRefund && (
                <GivelifyLabel
                    className="mobileFirstRowHeader tableHeaderText"
                    text={copy.donationDate}
                />
            )}
            <GivelifyLabel
                className="tableHeaderText envelopeNameHeader"
                text={copy.envelopeName}
            />
            <GivelifyLabel className="tableHeaderText" text={copy.donation} />
            <GivelifyLabel className="tableHeaderText" text={copy.fees} />
            <GivelifyLabel className="tableHeaderText" text={copy.total} />
            <GivelifyLabel
                className="mobileFirstRowText donationDetailsText"
                color="grey600"
                id={`receipt-no-${props.receiptNumber}`}
                text={`${copy.receipt} #: ${props.receiptNumber}`}
                variant="body2"
            />
            {isRefund && (
                <GivelifyLabel
                    className="mobileFirstRowText"
                    color="grey600"
                    id={`refund-date-${props.receiptNumber}`}
                    text={formatWithTimezone(props.date)}
                    variant="body2"
                />
            )}
            {envelopes.map((envelope, index) => {
                const feePercent = props.fees / props.total;
                const donation = formatMoney(envelope.amount || 0);
                const fees = formatMoney(
                    props.fees ? envelope.amount * feePercent : 0,
                );
                const refundAmount = formatMoney(
                    envelope.amount
                        ? envelope.amount - envelope.amount * feePercent
                        : 0,
                );
                return (
                    <React.Fragment key={index}>
                        <GivelifyLabel
                            className="envelopeNameText details-envelope-name"
                            color="grey600"
                            id={`envelope-name-${envelope.id}`}
                            text={envelope.name}
                            variant="body2"
                        />
                        <GivelifyLabel
                            className="details-donation-amount"
                            color="grey600"
                            id={`donation-${envelope.id}-${props.receiptNumber}`}
                            text={donation}
                            variant="body2"
                        />
                        <GivelifyLabel
                            className="details-receipt-number"
                            color="grey600"
                            id={`fee-${envelope.id}-${props.receiptNumber}`}
                            text={`-${fees}`}
                            variant="body2"
                        />
                        <GivelifyLabel
                            className="details-net-amount"
                            color="grey600"
                            id={`net-${envelope.id}-${props.receiptNumber}`}
                            text={refundAmount}
                            variant="body2"
                        />
                    </React.Fragment>
                );
            })}
            {envelopes.length > 1 && (
                <>
                    <div className="totalBorder" />
                    <GivelifyLabel
                        bold
                        className="donationResult"
                        color="grey600"
                        id={`total-donation-${props.receiptNumber}`}
                        text={formatMoney(props.total || 0)}
                        variant="body2"
                    />
                    <GivelifyLabel
                        bold
                        className="feesResult"
                        color="grey600"
                        id={`total-fee-${props.receiptNumber}`}
                        text={formatMoney(props.fees || 0)}
                        variant="body2"
                    />
                    <GivelifyLabel
                        bold
                        className="refundAmountResult"
                        color="grey600"
                        id={`total-net-${props.receiptNumber}`}
                        text={formatMoney(props.total - props.fees)}
                        variant="body2"
                    />
                </>
            )}
        </DonationDetailsContainer>
    );
};

export default DonationDetails;
